import React from "react";
import {
  FlatList,
  ImageBackground,
  StyleProp,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  TouchableOpacityProps,
  useWindowDimensions,
  View,
  ViewStyle,
} from "react-native";
import {
  NavigationProp,
  ParamListBase,
  RouteProp,
  useFocusEffect,
  useIsFocused,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import firebase from "firebase/app";
import algoliasearch from "algoliasearch/lite";
import {
  Configure,
  InstantSearch,
  useHierarchicalMenu,
  useInfiniteHits,
  useInstantSearch,
  useRefinementList,
  useSearchBox,
  useSortBy,
  useToggleRefinement,
} from "react-instantsearch-hooks";
import { Hit } from "instantsearch.js/es/types";
import { HierarchicalMenuItem } from "instantsearch.js/es/connectors/hierarchical-menu/connectHierarchicalMenu";
import { uniqueId } from "lodash";

import * as connectionDb from "src/db/connection.db";
import { PdStack, PdTag, PdTagLabel, PdText } from "src/components/layout";
import { Carousel } from "src/components/ui";
import { ALGOLIA_APP_ID, ALGOLIA_BRANDS, ALGOLIA_SEARCH_KEY } from "src/env";
import { useIsMobile, useUserProfile } from "src/hooks/ui";
import { kColors } from "src/kColors";
import { kRoute } from "src/kRoute";
import { kStyleConst } from "src/kStyle";
import { camelToTitle, formatMoney } from "src/util/gFormatting";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { kDefaultShopBrandsHeader } from "src/util/constants";
import { PdDivider } from "src/components/layout/table";
import { PdCheckbox, PdCheckboxLabel } from "src/components/forms/checkbox";
import { PdClose, PdFilter, PdGiftBox, PdSearch } from "src/components/Icons";
import { useSelector } from "react-redux";
import { tAlgoliaBrand, tRepConnection } from "tmt_api/src/types/types";
import { useAppDispatch } from "src/redux";
import { fetchExclusiveBrands } from "src/redux/exclusiveBrands/thunkActions";
import { PdSelect, tPdSelectOption } from "src/components/forms/select";
import { ScrollView } from "react-native-gesture-handler";
import { ShopSubHeader } from "src/components/connected";
import { tSvgProps } from "src/components/types";
import { useShopMobileFiltersModal } from "src/hooks/modals";
import { useStats } from "src/hooks/algolia";

type tBrandsRouteParams = {
  brands: {
    category?: string;
  };
};

type tBrandIndexUiState = {
  query?: string;
  sortBy: string;
  hierarchicalMenu: {
    "categories.lvl0": string[];
  };
  refinementList: {
    objectID?: string[];
    brandEthos: string[];
  };
  toggle: {
    isExclusive: boolean;
  };
};

type tBrandUiState = {
  [key: string]: tBrandIndexUiState;
};

const searchClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY);
const BANNER_HEIGHT = 380;
const PAGE_SIZE = 24;
const PAGE_HEADER_SIZE = 260;
const FILTERS_WIDTH = 300;
const GAP = 30;
const MY_BRANDS_EMPTY = "my-brands-empty";

const paginate = <T extends unknown>(array: T[], pageSize: number, pageNumber: number) => {
  return array.slice((pageNumber - 1) * pageSize, Math.min(pageNumber * pageSize, array.length));
};

const randomizeFromInterval = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1) + min);

const INITIAL_BRAND_UI_STATE = {
  query: "",
  sortBy: ALGOLIA_BRANDS.DEFAULT,
  hierarchicalMenu: { "categories.lvl0": [] },
  refinementList: { objectID: [], brandEthos: [] },
  toggle: { isExclusive: false },
};

const useBrandInstantSearch = () => {
  return useInstantSearch<tBrandUiState>();
};

const useGridLayout = () => {
  const { width } = useWindowDimensions();
  let numColumns = 5;
  if (width >= 1600) numColumns = 4;
  if (width >= 1200 && width < 1600) numColumns = 3;
  if (width >= 600 && width < 1200) numColumns = 2;
  if (width < 600) numColumns = 1;
  return numColumns;
};

type tBrandCard = {
  hit: Hit<tAlgoliaBrand>;
  randomizeCarouselImages?: boolean;
  style?: StyleProp<ViewStyle>;
  onPress?: (hit: Hit<tAlgoliaBrand>) => void;
};

const BrandCard: React.FC<tBrandCard> = ({ hit, randomizeCarouselImages, style, onPress }) => {
  const aImageUrl = hit["images.heroes"];
  const defaultCarouselIndex = randomizeCarouselImages ? randomizeFromInterval(1, aImageUrl.length - 1) : 0;
  const hasPublicPublishedPromotions =
    (
      hit.createdPromotions?.filter(
        (promotion) =>
          !promotion.isPrivate &&
          !promotion.isDeactivated &&
          promotion.endDate > firebase.firestore.Timestamp.now().toMillis(),
      ) ?? []
    ).length > 0;

  const handleOnPressShopBrand = () => {
    onPress?.(hit);
  };

  return (
    <View style={style}>
      <TouchableOpacity onPress={handleOnPressShopBrand}>
        <Carousel defaultIndex={defaultCarouselIndex} imageUrls={aImageUrl.map((image) => image.downloadURL)} />
        <PdStack direction="vertical" style={styles.brandCardTagsContainer}>
          {hit.isExclusive && (
            <PdTag>
              <PdTagLabel style={styles.brandCardTagLabel}>Exclusive</PdTagLabel>
            </PdTag>
          )}
          {hasPublicPublishedPromotions && (
            <PdTag style={styles.brandCardPromotionTag}>
              <PdTagLabel style={styles.brandCardTagLabel}>Promotion</PdTagLabel>
            </PdTag>
          )}
        </PdStack>
      </TouchableOpacity>
      <View>
        <View>
          <PdText style={[styles.textSm, styles.fontBold, styles.colorBlack, styles.leading10]}>{hit.name}</PdText>
          <PdText style={[styles.textSm, styles.fontLight, styles.colorBlack, styles.mb1]}>{`Opening Min. ${formatMoney(
            hit.openingMin,
          )}`}</PdText>
          <PdText style={[styles.textSm, styles.fontLight, styles.colorBlack, styles.mb1]}>{`Reorder Min. ${formatMoney(
            hit.reorderMin,
          )}`}</PdText>
        </View>
        <PdButton variant="link" onPress={handleOnPressShopBrand} style={[styles.mt4, styles.mrAuto]}>
          <PdButtonLabel>Shop Brand</PdButtonLabel>
        </PdButton>
      </View>
    </View>
  );
};

const HierarchicalCategoryMenuItem: React.FC<{
  leftIcon?: React.ReactElement<tSvgProps>;
  item: HierarchicalMenuItem;
  onPress: (item: HierarchicalMenuItem) => void;
  onPressSubcategory: (item: HierarchicalMenuItem) => void;
}> = ({ leftIcon, item, onPress, onPressSubcategory }) => {
  const handleOnPress = () => {
    onPress(item);
  };

  const handleOnPressSubcategory = (subcategoryItem: HierarchicalMenuItem) => {
    onPressSubcategory(subcategoryItem);
  };

  return (
    <View>
      <TouchableOpacity onPress={handleOnPress}>
        <View style={[styles.flexRow, styles.alignCenter]}>
          {!!leftIcon && (
            <View style={styles.hierarchicalMenuItemIcon}>
              {React.cloneElement(leftIcon, { color: kColors.brandDark, style: leftIcon.props.style })}
            </View>
          )}
          <PdText style={[styles.colorBrand, styles.textBase, item.isRefined ? styles.fontBold : styles.fontMedium]}>
            {item.label}
          </PdText>
          <PdText style={[styles.colorBrand, styles.textBase, styles.fontLight]}>{` (${item.count})`}</PdText>
        </View>
      </TouchableOpacity>
      {item.data && (
        <View style={styles.pl3}>
          {item.data.map((subItem) => {
            return (
              <TouchableOpacity
                key={subItem.value}
                onPress={() => {
                  handleOnPressSubcategory(subItem);
                }}
              >
                <View style={[styles.flexRow, styles.alignCenter, styles.justifyBetween, styles.mt4]}>
                  <PdText
                    style={[
                      styles.colorBrand,
                      styles.textSm,
                      subItem.isRefined ? styles.fontSemibold : styles.fontLight,
                    ]}
                  >
                    {subItem.label}
                  </PdText>
                  <PdText style={[styles.colorBrand, styles.textSm, styles.fontLight]}>{subItem.count}</PdText>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      )}
    </View>
  );
};

const HierarchicalCategoriesMenu: React.FC = () => {
  const { items, refine } = useHierarchicalMenu({
    attributes: ["categories.lvl0", "categories.lvl1"],
    separator: " - ",
    showParentLevel: true,
    limit: 100,
  });
  const { indexUiState, setIndexUiState } = useBrandInstantSearch();
  const navigation = useNavigation();
  const route = useRoute<RouteProp<tBrandsRouteParams, "brands">>();

  const handleOnPress = (item: HierarchicalMenuItem) => {
    setIndexUiState((prev) => ({ ...prev, query: "" }));
    const category = item.isRefined ? undefined : item.value;
    navigation.setParams({ category });
  };

  const handleOnSubcategoryPress = (item: HierarchicalMenuItem) => {
    // TODO: unselct sub category should not clear parent category
    const category = item.isRefined ? undefined : item.value;
    navigation.setParams({ category });
  };

  React.useEffect(() => {
    if (indexUiState.query) refine("");
  }, [indexUiState.query, refine]);

  React.useEffect(() => {
    refine(route.params?.category ?? "");
  }, [refine, route.params?.category]);

  if (items.length === 0) {
    return null;
  }

  const maybeHolidayMenu = items.find((item) => item.label === "Holiday");

  return (
    <View style={styles.pt1}>
      {maybeHolidayMenu && (
        <>
          <HierarchicalCategoryMenuItem
            leftIcon={<PdGiftBox color={kColors.brandDarkRed} style={styles.giftIcon} />}
            item={maybeHolidayMenu}
            onPress={handleOnPress}
            onPressSubcategory={handleOnSubcategoryPress}
          />
          <PdDivider style={styles.my2} />
        </>
      )}
      {items
        .filter((item) => item.label !== "Holiday")
        .map((item) => {
          return (
            <React.Fragment key={item.label}>
              <HierarchicalCategoryMenuItem
                item={item}
                onPress={handleOnPress}
                onPressSubcategory={handleOnSubcategoryPress}
              />
              <PdDivider style={styles.my2} />
            </React.Fragment>
          );
        })}
    </View>
  );
};

const BrandEthosRefinementList: React.FC<{ isMobile?: boolean }> = ({ isMobile }) => {
  const { items, refine } = useRefinementList({ attribute: "brandEthos" });

  if (items.length === 0) {
    return null;
  }

  return (
    <View style={styles.mt2}>
      {!isMobile && <PdText style={[styles.textBase, styles.fontSemibold, styles.mb1]}>Filter</PdText>}
      {items.map((item) => {
        return (
          <PdCheckbox
            key={item.label}
            value={item.isRefined}
            onPress={() => {
              refine(item.value);
            }}
            style={styles.my1}
          >
            <PdCheckboxLabel style={[styles.textSm, styles.capitalize]}>{camelToTitle(item.label)}</PdCheckboxLabel>
            <PdText style={styles.mlAuto}>{item.count}</PdText>
          </PdCheckbox>
        );
      })}
    </View>
  );
};

const ExclusiveCheckbox: React.FC = () => {
  const { value, refine } = useToggleRefinement({ attribute: "isExclusive" });

  const handleOnPress = () => {
    refine({ isRefined: value.isRefined });
  };

  if (value.count === null) {
    return null;
  }

  return (
    <PdCheckbox value={value.isRefined} onChange={handleOnPress} style={styles.my1}>
      <PdCheckboxLabel style={[styles.textSm, styles.capitalize]}>Exclusive</PdCheckboxLabel>
      <PdText style={styles.mlAuto}>{value.onFacetValue.count}</PdText>
    </PdCheckbox>
  );
};

const orderOptions = [
  { value: ALGOLIA_BRANDS.DEFAULT, label: "All" },
  { value: ALGOLIA_BRANDS.NEW, label: "New" },
  { value: ALGOLIA_BRANDS.PRICE_DESC, label: "High to Low" },
  { value: ALGOLIA_BRANDS.PRICE_ASC, label: "Low to High" },
];

const SortBy: React.FC = () => {
  const { options, refine, currentRefinement } = useSortBy({ items: orderOptions });

  const handleOnChange = (newOption: tPdSelectOption | null) => {
    if (newOption) refine(newOption.value);
  };

  return (
    <PdSelect
      containerStyle={styles.orderFilter}
      value={options.find((option) => option.value === currentRefinement)}
      onChange={handleOnChange}
      options={options}
      isClearable={false}
    />
  );
};

const PageFilters: React.FC = () => {
  return (
    <View style={[{ width: FILTERS_WIDTH, padding: GAP }, styles.mt1]}>
      <SortBy />
      <HierarchicalCategoriesMenu />
      <BrandEthosRefinementList />
      <ExclusiveCheckbox />
    </View>
  );
};

const PageMobileFilters: React.FC = () => {
  const navigation = useNavigation();
  const route = useRoute<RouteProp<tBrandsRouteParams, "brands">>();
  const { indexUiState, setIndexUiState } = useBrandInstantSearch();
  const { refine: refineCategory } = useHierarchicalMenu({
    attributes: ["categories.lvl0", "categories.lvl1"],
    separator: " - ",
    showParentLevel: true,
    limit: 100,
  });
  const shopMobileFilters = useShopMobileFiltersModal();
  useSortBy({ items: orderOptions });
  useToggleRefinement({ attribute: "isExclusive" });
  useRefinementList({ attribute: "brandEthos" });

  const handleOnPress = async () => {
    const response = await shopMobileFilters.openAsync({
      defaultSearchState: {
        sortBy: indexUiState.sortBy ?? "",
        hierarchicalMenu: {
          "categories.lvl0": [route.params?.category ?? ""],
        },
        refinementList: {
          brandEthos: indexUiState.refinementList?.brandEthos ?? [],
        },
        toggle: {
          isExclusive: !!indexUiState.toggle?.isExclusive,
        },
      },
    });
    if (response.status === "closed") return;

    navigation.setParams({ category: response.data.hierarchicalMenu?.["categories.lvl0"]?.join(" - ") });
    setIndexUiState({
      ...INITIAL_BRAND_UI_STATE,
      sortBy: response.data.sortBy,
      refinementList: {
        ...INITIAL_BRAND_UI_STATE.refinementList,
        brandEthos: response.data.refinementList?.brandEthos ?? [],
      },
      toggle: { isExclusive: response.data.toggle?.isExclusive ?? false },
    });
  };

  const sortBy = orderOptions.find((option) => option.value === indexUiState.sortBy) || orderOptions[0];

  React.useEffect(() => {
    refineCategory(route.params?.category ?? "");
  }, [refineCategory, route.params?.category]);

  return (
    <View style={styles.m6}>
      <PdButton
        variant="ghost"
        leftIcon={<PdFilter style={styles.filterButtonIcon} />}
        onPress={handleOnPress}
        style={styles.mlAuto}
        contentContainerStyle={styles.filterButton}
      >
        <PdButtonLabel>Filter & Sort</PdButtonLabel>
      </PdButton>
      <View style={[styles.flexRow, styles.flex1, styles.flexWrap, styles.mt4]}>
        {sortBy && (
          <PdTag style={[styles.mr2, styles.mb2]}>
            <PdTagLabel>{sortBy.label}</PdTagLabel>
          </PdTag>
        )}
        {route.params?.category && (
          <TouchableOpacity
            style={[styles.mr2, styles.mb2]}
            onPress={() => navigation.setParams({ category: undefined })}
          >
            <PdTag style={[styles.flexRow, styles.alignCenter]}>
              <PdTagLabel>{route.params.category}</PdTagLabel>
              <PdClose color={kColors.white} style={styles.tagCloseIcon} />
            </PdTag>
          </TouchableOpacity>
        )}
        {indexUiState.refinementList?.brandEthos?.map((ethosItem) => (
          <TouchableOpacity
            key={ethosItem}
            style={[styles.mr2, styles.mb2]}
            onPress={() =>
              setIndexUiState((prev) => ({
                ...prev,
                refinementList: {
                  ...prev.refinementList,
                  brandEthos: prev.refinementList?.brandEthos.filter((prevEthos) => prevEthos !== ethosItem) ?? [],
                },
              }))
            }
          >
            <PdTag style={[styles.flexRow, styles.alignCenter]}>
              <PdTagLabel>{camelToTitle(ethosItem)}</PdTagLabel>
              <PdClose color={kColors.white} style={styles.tagCloseIcon} />
            </PdTag>
          </TouchableOpacity>
        ))}
        {indexUiState.toggle?.isExclusive && (
          <TouchableOpacity
            onPress={() => setIndexUiState((prev) => ({ ...prev, toggle: { ...prev.toggle, isExclusive: false } }))}
            style={[styles.mr2, styles.mb2]}
          >
            <PdTag style={[styles.flexRow, styles.alignCenter]}>
              <PdTagLabel>Exclusive</PdTagLabel>
              <PdClose color={kColors.white} style={styles.tagCloseIcon} />
            </PdTag>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const SearchBox: React.FC = () => {
  const { query, refine } = useSearchBox();
  const [value, setValue] = React.useState(query);
  const route = useRoute<RouteProp<tBrandsRouteParams, "brands">>();

  const handleOnChangeText = (newValue: string) => {
    setValue(newValue);
    refine(newValue);
  };

  React.useEffect(() => {
    if (route.params?.category) {
      setValue("");
      refine("");
    }
  }, [refine, route.params?.category]);

  return <TextInput placeholder="Search" style={styles.searchBox} value={value} onChangeText={handleOnChangeText} />;
};

const Stats: React.FC = () => {
  const { nbHits } = useStats();
  return <PdText>{`(${nbHits})`}</PdText>;
};

const PageSearchBox: React.FC = () => {
  return (
    <View style={[styles.pageSearchBox, { margin: GAP }]}>
      <View style={styles.pageSearchBoxIconContainer}>
        <PdSearch style={styles.pageSearchBoxIcon} />
      </View>
      <SearchBox />
      <Stats />
    </View>
  );
};

const HighlightReelExclusive: React.FC<TouchableOpacityProps> = (props) => {
  const navigation = useNavigation();
  const { setIndexUiState } = useBrandInstantSearch();

  const handleOnPress = () => {
    navigation.setParams({ category: undefined });
    setIndexUiState((prev) => ({ ...INITIAL_BRAND_UI_STATE, toggle: { isExclusive: !prev.toggle?.isExclusive } }));
  };

  return (
    <TouchableOpacity onPress={handleOnPress} {...props}>
      <ImageBackground
        style={styles.sizeMd}
        source={{ uri: "/images/highlight-reel-exclusive.png" }}
        resizeMode="cover"
      />
    </TouchableOpacity>
  );
};

const HighlightReelNew: React.FC<TouchableOpacityProps> = (props) => {
  const navigation = useNavigation();
  const { setIndexUiState } = useBrandInstantSearch();

  const handleOnPress = () => {
    navigation.setParams({ category: undefined });
    setIndexUiState({ ...INITIAL_BRAND_UI_STATE, sortBy: ALGOLIA_BRANDS.NEW });
  };

  return (
    <TouchableOpacity onPress={handleOnPress} {...props}>
      <ImageBackground style={styles.sizeMd} source={{ uri: "/images/highlight-reel-new.png" }} resizeMode="cover" />
    </TouchableOpacity>
  );
};

const HighlightReelAwardWinning: React.FC<TouchableOpacityProps> = (props) => {
  const navigation = useNavigation();
  const { setIndexUiState } = useBrandInstantSearch();

  const onPress = () => {
    navigation.setParams({ category: undefined });
    setIndexUiState({
      ...INITIAL_BRAND_UI_STATE,
      refinementList: { ...INITIAL_BRAND_UI_STATE.refinementList, brandEthos: ["awardWinning"] },
    });
  };

  return (
    <TouchableOpacity onPress={onPress} {...props}>
      <ImageBackground
        style={styles.sizeMd}
        source={{ uri: "/images/highlight-reel-award-winning.png" }}
        resizeMode="cover"
      />
    </TouchableOpacity>
  );
};

const HighlightReelHome: React.FC<TouchableOpacityProps> = (props) => {
  const navigation = useNavigation();
  const { setIndexUiState } = useBrandInstantSearch();

  const onPress = () => {
    navigation.setParams({ category: "Home" });
    setIndexUiState(INITIAL_BRAND_UI_STATE);
  };

  return (
    <TouchableOpacity onPress={onPress} {...props}>
      <ImageBackground style={styles.sizeMd} source={{ uri: "/images/highlight-reel-home.png" }} resizeMode="cover" />
    </TouchableOpacity>
  );
};

const HighlightReelFragrances: React.FC<TouchableOpacityProps> = (props) => {
  const navigation = useNavigation();
  const { setIndexUiState } = useBrandInstantSearch();

  const onPress = () => {
    navigation.setParams({ category: "Fragrances" });
    setIndexUiState(INITIAL_BRAND_UI_STATE);
  };

  return (
    <TouchableOpacity onPress={onPress} {...props}>
      <ImageBackground
        style={styles.sizeMd}
        source={{ uri: "/images/highlight-reel-fragrances.png" }}
        resizeMode="cover"
      />
    </TouchableOpacity>
  );
};

const HighlightReelWellness: React.FC<TouchableOpacityProps> = (props) => {
  const navigation = useNavigation();
  const { setIndexUiState } = useBrandInstantSearch();

  const onPress = () => {
    navigation.setParams({ category: "Wellness" });
    setIndexUiState(INITIAL_BRAND_UI_STATE);
  };

  return (
    <TouchableOpacity onPress={onPress} {...props}>
      <ImageBackground
        style={styles.sizeMd}
        source={{ uri: "/images/highlight-reel-wellness.png" }}
        resizeMode="cover"
      />
    </TouchableOpacity>
  );
};

const HighlightReels: React.FC = () => {
  const isFocused = useIsFocused();
  const navigation = useNavigation();
  const rConfigs = useSelector((state) => state.ConfigsReducer);
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const { setIndexUiState } = useBrandInstantSearch();
  const [connectionBrandIds, setConnectionBrandIds] = React.useState<string[]>([]);

  const handleOnPressMyBrands = () => {
    navigation.setParams({ category: undefined });
    setIndexUiState({
      ...INITIAL_BRAND_UI_STATE,
      refinementList: {
        ...INITIAL_BRAND_UI_STATE.refinementList,
        objectID: connectionBrandIds.length === 0 ? [MY_BRANDS_EMPTY] : connectionBrandIds,
      },
    });
  };

  const handleOnPressBestSellers = () => {
    navigation.setParams({ category: undefined });
    setIndexUiState({
      ...INITIAL_BRAND_UI_STATE,
      refinementList: {
        ...INITIAL_BRAND_UI_STATE.refinementList,
        objectID: rConfigs.data.shop?.bestSellerBrandsIds ?? [],
      },
    });
  };

  // const handleOnPressHoliday = () => {
  //   navigation.setParams({ category: "Holiday" });
  //   // setIndexUiState({ ...INITIAL_BRAND_UI_STATE, hierarchicalMenu: { "categories.lvl0": ["Holiday"] } });
  // };

  const handleOnPressGift = () => {
    navigation.setParams({ category: undefined });
    setIndexUiState({
      ...INITIAL_BRAND_UI_STATE,
      refinementList: { ...INITIAL_BRAND_UI_STATE.refinementList, objectID: rConfigs.data.shop?.giftBrandsIds ?? [] },
    });
  };

  React.useEffect(() => {
    (async () => {
      const connectionsQuery = await connectionDb.queryBrandRetailerConnection({ retailerId: rCompanyId }).get();
      setConnectionBrandIds(connectionsQuery.docs.map((doc) => doc.data().brand.id));
    })();
  }, [rCompanyId, setConnectionBrandIds]);

  React.useEffect(() => {
    if (!isFocused) {
      setConnectionBrandIds([]);
    }
  }, [isFocused]);

  return (
    <ScrollView
      horizontal
      contentContainerStyle={[
        styles.alignCenter,
        styles.justifyCenter,
        styles.flexGrow1,
        { paddingLeft: GAP, paddingVertical: GAP },
      ]}
    >
      <TouchableOpacity onPress={handleOnPressMyBrands} style={{ marginRight: GAP * 2 }}>
        <ImageBackground
          style={styles.sizeMd}
          source={{ uri: "/images/highlight-reel-my-brands.png" }}
          resizeMode="cover"
        />
      </TouchableOpacity>
      <HighlightReelNew style={{ marginRight: GAP * 2 }} />
      <TouchableOpacity onPress={handleOnPressBestSellers} style={{ marginRight: GAP * 2 }}>
        <ImageBackground
          style={styles.sizeMd}
          source={{ uri: "/images/highlight-reel-best-sellers.png" }}
          resizeMode="cover"
        />
      </TouchableOpacity>
      {/* <TouchableOpacity onPress={handleOnPressHoliday} style={{ paddingRight: GAP * 2 }}>
        <ImageBackground style={styles.sizeMd} source={{ uri: "/images/holiday.png" }} resizeMode="cover" />
      </TouchableOpacity> */}
      <TouchableOpacity onPress={handleOnPressGift} style={{ marginRight: GAP * 2 }}>
        <ImageBackground
          style={styles.sizeMd}
          source={{ uri: "/images/highlight-reel-gifts.png" }}
          resizeMode="cover"
        />
      </TouchableOpacity>
      <HighlightReelExclusive style={{ marginRight: GAP * 2 }} />
      <HighlightReelAwardWinning style={{ marginRight: GAP * 2 }} />
      <HighlightReelHome style={{ marginRight: GAP * 2 }} />
      <HighlightReelFragrances style={{ marginRight: GAP * 2 }} />
      <HighlightReelWellness style={{ marginRight: GAP * 2 }} />
    </ScrollView>
  );
};

const PageHeader: React.FC = () => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <View>
        <HighlightReels />
        <View>
          <PageMobileFilters />
          <PageSearchBox />
        </View>
      </View>
    );
  }

  return (
    <View style={styles._m3}>
      <HighlightReels />
      <View style={[styles.alignStart, styles.flexRow]}>
        <PageFilters />
        <PageSearchBox />
      </View>
    </View>
  );
};

const PageEmpty: React.FC<{ numColumns: number }> = ({ numColumns }) => {
  const { indexUiState } = useBrandInstantSearch();
  const navigation = useNavigation();
  const exclusiveBrands = useSelector((state) => state.ExclusiveBrandReducer.brands);
  const key = React.useMemo(() => uniqueId(`${numColumns}_exclusive_`), [numColumns]);

  const handleOnPressBrand: tBrandCard["onPress"] = (hit) => {
    navigation.navigate(kRoute.P100, { brand: hit.name });
  };

  if (indexUiState.refinementList?.objectID?.length === 0) {
    return (
      <View style={[styles.flex1, styles.flexWrap, styles.my10]}>
        <PdText style={[styles.textXs, styles.textCenter, styles.italic, styles.colorGray, styles.mx10]}>
          You haven&apos;t connected with any brands yet. Send connection requests to brands you&apos;d like to connect
          & buy from. In the meantime, browse our exclusive brands only available on The Main Tab.
        </PdText>
      </View>
    );
  }

  return (
    <View style={[styles.flex1, styles.flexWrap]}>
      <View style={[styles.alignCenter, styles.my10]}>
        <PdText style={[styles.textXs, styles.textCenter, styles.italic, styles.colorGray, styles.mx10]}>
          No search results found
        </PdText>
      </View>
      {exclusiveBrands.length > 0 && (
        <>
          <View style={styles.flexRow}>
            <PdText
              style={[styles.textSm, styles.fontLight, styles.colorBlack, styles.mb4, { paddingHorizontal: GAP / 2 }]}
            >
              {"In the mean time, some "}
              <PdText style={[styles.textSm, styles.fontBold, styles.colorBlack]}>Exclusive Brands</PdText>!
            </PdText>
          </View>
          <FlatList
            numColumns={numColumns}
            data={exclusiveBrands}
            keyExtractor={(item) => item.objectID}
            renderItem={({ item }) => (
              <BrandCard hit={item} onPress={handleOnPressBrand} style={{ flex: 1 / numColumns, padding: GAP / 2 }} />
            )}
            key={key}
            onEndReachedThreshold={20}
          />
        </>
      )}
    </View>
  );
};

const Page: React.FC = () => {
  const { hits, isLastPage, showMore } = useInfiniteHits<tAlgoliaBrand>();
  const numColumns = useGridLayout();
  const navigation = useNavigation();
  const isMobile = useIsMobile();
  const { isBrand, isRep } = useUserProfile();
  const { indexUiState } = useInstantSearch();
  const flatListRef = React.useRef<FlatList>(null);
  useRefinementList({ attribute: "objectID" });

  const isFiltering =
    indexUiState.query ||
    (indexUiState.hierarchicalMenu?.["categories.lvl0"]?.length ?? 0) > 0 ||
    (indexUiState.refinementList?.objectID?.length ?? 0) > 0 ||
    (indexUiState.refinementList?.brandEthos?.length ?? 0) > 0 ||
    !!indexUiState.toggle?.isExclusive;

  const [oldResults, setOldResults] = React.useState<{
    data: Hit<tAlgoliaBrand>[];
    paginated: Hit<tAlgoliaBrand>[];
    currentPage: number;
    totalPages: number;
  }>({
    data: [],
    paginated: [],
    currentPage: 0,
    totalPages: 0,
  });
  const filteredHits = React.useMemo(() => hits.filter((hit) => hit.pauseMode !== "pause"), [hits]);
  const data = React.useMemo(() => {
    return isFiltering ? filteredHits : [...filteredHits, ...oldResults.paginated];
  }, [filteredHits, isFiltering, oldResults.paginated]);
  const key = React.useMemo(() => uniqueId(`${numColumns}_`), [numColumns]);

  const fetchMore = () => {
    if (!isLastPage) {
      showMore();
    } else if (!isFiltering) {
      setOldResults((prev) => {
        const didEndReached = prev.currentPage === prev.totalPages;
        const nextPaginated: Hit<tAlgoliaBrand>[] = [];
        paginate(prev.data, PAGE_SIZE, prev.currentPage).forEach((item) => {
          if (!item.isExclusive) nextPaginated.push({ ...item, objectID: uniqueId("old_") });
        });
        return {
          data: prev.data,
          paginated: [...prev.paginated, ...nextPaginated],
          currentPage: didEndReached ? 0 : ++prev.currentPage,
          totalPages: prev.totalPages,
        };
      });
    }
  };

  const scrollToTop = () => {
    flatListRef.current?.scrollToOffset({ animated: true, offset: 0 });
  };

  const handleOnPressBrand: tBrandCard["onPress"] = (hit) => {
    navigation.navigate(kRoute.P100, { brand: hit.name });
  };

  React.useEffect(() => {
    if (!isBrand && !isRep && isLastPage && !isFiltering) {
      const paginated: Hit<tAlgoliaBrand>[] = [];
      paginate(filteredHits, PAGE_SIZE, 0).forEach((item) => {
        if (!item.isExclusive) paginated.push({ ...item, objectID: uniqueId("old_") });
      });
      setOldResults({
        data: filteredHits,
        paginated,
        currentPage: 1,
        totalPages: Math.ceil(filteredHits.length / PAGE_SIZE),
      });
    }
  }, [filteredHits, isLastPage, isBrand, isFiltering, isRep]);

  return (
    <>
      <FlatList
        ref={flatListRef}
        ListHeaderComponent={PageHeader}
        ListHeaderComponentStyle={
          isMobile ? {} : [styles.absolute, styles.insetX0, { top: -PAGE_HEADER_SIZE + GAP / 2 }]
        }
        ListEmptyComponent={<PageEmpty numColumns={numColumns} />}
        numColumns={numColumns}
        contentContainerStyle={
          isMobile
            ? {}
            : { marginTop: PAGE_HEADER_SIZE, margin: GAP / 2, paddingTop: GAP / 2, paddingLeft: FILTERS_WIDTH }
        }
        data={data}
        keyExtractor={(item) => item.objectID}
        renderItem={({ item }: { item: Hit<tAlgoliaBrand> }) => (
          <BrandCard
            hit={item}
            onPress={handleOnPressBrand}
            randomizeCarouselImages={item.objectID.startsWith("old_")}
            style={{ flex: 1 / numColumns, padding: GAP / 2 }}
          />
        )}
        key={key}
        onEndReached={fetchMore}
        onEndReachedThreshold={20}
      />
      <PdButton onPress={scrollToTop} size="sm" style={styles.backToTopButton}>
        <PdButtonLabel>Back to Top</PdButtonLabel>
      </PdButton>
    </>
  );
};

const ContextShopSubHeader: React.FC = () => {
  const { setIndexUiState } = useBrandInstantSearch();

  const onPress = (item: string | undefined) => {
    if (item === undefined) {
      setIndexUiState(INITIAL_BRAND_UI_STATE);
    }
  };

  return <ShopSubHeader onPress={onPress} />;
};

const RepsBrands: React.FC<{ key: string; numColumns: number }> = ({ key, numColumns }) => {
  const isMobile = useIsMobile();
  const navigation = useNavigation();
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const [data, setData] = React.useState<tRepConnection[]>([]);

  const handleOnPressBrand: tBrandCard["onPress"] = (hit) => {
    navigation.navigate(kRoute.P100, { brand: hit.name });
  };

  useFocusEffect(
    React.useCallback(() => {
      (async () => {
        const connectionsQuery = await connectionDb
          .queryBrandRepConnection({ repId: rCompanyId, status: "Approved" })
          .get();
        setData(connectionsQuery.docs.map((doc) => doc.data()));
      })();
    }, [rCompanyId, setData]),
  );

  return (
    <FlatList
      numColumns={numColumns}
      contentContainerStyle={isMobile ? {} : { paddingTop: GAP / 2, paddingHorizontal: GAP / 2 }}
      data={data.map<Hit<tAlgoliaBrand>>((connection) => ({
        objectID: connection.business.id,
        brandStory: connection.business.brandStory ?? "",
        name: connection.business.name,
        productsNames: [],
        productsKeywords: [],
        categories: connection.business.categories,
        brandEthos: connection.business.brandEthos,
        isExclusive: connection.business.isExclusive ?? false,
        openingMin: connection.business.openingMin,
        reorderMin: connection.business.reorderMin,
        "images.logoDownloadURL": connection.business.images.logoDownloadURL,
        "images.heroes": connection.business.images.heroes,
        __position: 0,
      }))}
      keyExtractor={(item) => item.objectID}
      renderItem={({ item }) => (
        <BrandCard hit={item} onPress={handleOnPressBrand} style={{ flex: 1 / numColumns, padding: GAP / 2 }} />
      )}
      key={key}
      onEndReachedThreshold={20}
    />
  );
};

const P99_Brands: React.FC<{ navigation: NavigationProp<ParamListBase> }> = ({ navigation }) => {
  const dispatch = useAppDispatch();
  const rUserAccountType = useSelector((state) => state.UserReducer.profile.accountType);
  const rCompany = useSelector((state) => state.CompanyReducer);
  const numColumns = useGridLayout();
  const key = React.useMemo(() => uniqueId(`${numColumns}_`), [numColumns]);
  const isMobile = useIsMobile();

  const handleOnPressBrand: tBrandCard["onPress"] = (hit) => {
    navigation.navigate(kRoute.P100, { brand: hit.name });
  };

  React.useEffect(() => {
    searchClient.clearCache();
  }, []);

  React.useEffect(() => {
    dispatch(fetchExclusiveBrands());
  }, [dispatch]);

  if (rUserAccountType === "brand" && rCompany.type === "brand") {
    const data: Hit<tAlgoliaBrand>[] = [
      {
        objectID: rCompany.id,
        brandStory: rCompany.brandStory ?? "",
        name: rCompany.name,
        productsNames: [],
        productsKeywords: [],
        categories: rCompany.categories,
        brandEthos: rCompany.brandEthos,
        isExclusive: rCompany.isExclusive ?? false,
        openingMin: rCompany.openingMin,
        reorderMin: rCompany.reorderMin,
        "images.logoDownloadURL": rCompany.images.logoDownloadURL,
        "images.heroes": rCompany.images.heroes,
        __position: 0,
      },
    ];
    return (
      <FlatList
        ListHeaderComponent={
          <ImageBackground
            style={styles.pageHeaderImage}
            source={{ uri: kDefaultShopBrandsHeader }}
            resizeMode="cover"
          />
        }
        ListHeaderComponentStyle={isMobile ? {} : [styles.absolute, styles.insetX0, { top: -BANNER_HEIGHT }]}
        numColumns={numColumns}
        contentContainerStyle={
          isMobile ? {} : { marginTop: BANNER_HEIGHT, paddingTop: GAP / 2, paddingHorizontal: GAP / 2 }
        }
        data={data}
        keyExtractor={(item) => item.objectID}
        renderItem={({ item }) => (
          <BrandCard hit={item} onPress={handleOnPressBrand} style={{ flex: 1 / numColumns, padding: GAP / 2 }} />
        )}
        key={key}
        onEndReachedThreshold={20}
      />
    );
  }

  if (rUserAccountType === "rep" && rCompany.type === "rep") {
    return <RepsBrands key={key} numColumns={numColumns} />;
  }

  return (
    <InstantSearch searchClient={searchClient} indexName={ALGOLIA_BRANDS.DEFAULT}>
      <Configure clickAnalytics />
      <View style={[styles.flexRow, styles.flex1]}>
        <View style={styles.flex1}>
          <ContextShopSubHeader />
          <Page />
        </View>
      </View>
    </InstantSearch>
  );
};

export const P99 = React.memo(P99_Brands);

const styles = StyleSheet.create({
  _m3: {
    margin: -(3 * kStyleConst.kSpacing),
  },
  absolute: {
    position: "absolute",
  },
  alignCenter: {
    alignItems: "center",
  },
  alignStart: {
    alignItems: "flex-start",
  },
  backToTopButton: {
    bottom: 15,
    position: "absolute",
    right: 30,
  },
  brandCardPromotionTag: {
    backgroundColor: kColors.brandPurple,
  },
  brandCardTagLabel: {
    textTransform: "uppercase",
  },
  brandCardTagsContainer: {
    left: 14,
    position: "absolute",
    top: 14,
  },
  capitalize: {
    textTransform: "capitalize",
  },
  colorBlack: {
    color: kColors.black,
  },
  colorBrand: {
    color: kColors.brandDark,
  },
  colorGray: {
    color: kColors.mediumGrey,
  },
  filterButton: {
    justifyContent: "flex-end",
    paddingHorizontal: 0,
  },
  filterButtonIcon: {
    width: 15,
  },
  flex1: {
    flex: 1,
  },
  flexGrow1: {
    flexGrow: 1,
  },
  flexRow: {
    flexDirection: "row",
  },
  flexWrap: {
    flexWrap: "wrap",
  },
  fontBold: {
    fontWeight: "700",
  },
  fontLight: {
    fontWeight: "300",
  },
  fontMedium: {
    fontWeight: "500",
  },
  fontSemibold: {
    fontWeight: "600",
  },
  giftIcon: {
    height: 20,
  },
  hierarchicalMenuItemIcon: {
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  },
  insetX0: {
    left: 0,
    right: 0,
  },
  italic: {
    fontStyle: "italic",
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  justifyCenter: {
    justifyContent: "center",
  },
  leading10: {
    lineHeight: 40,
  },
  m6: {
    margin: 6 * kStyleConst.kSpacing,
  },
  mb1: {
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  mb2: {
    marginBottom: 2 * kStyleConst.kSpacing,
  },
  mb4: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  mr2: {
    marginRight: 2 * kStyleConst.kSpacing,
  },
  mrAuto: {
    marginRight: "auto",
  },
  mt1: {
    marginTop: kStyleConst.kSpacing * 1,
  },
  mt2: {
    marginTop: 2 * kStyleConst.kSpacing,
  },
  mt4: {
    marginTop: 4 * kStyleConst.kSpacing,
  },
  mx10: {
    marginHorizontal: 10 * kStyleConst.kSpacing,
  },
  my1: {
    marginVertical: 1 * kStyleConst.kSpacing,
  },
  my10: {
    marginVertical: 10 * kStyleConst.kSpacing,
  },
  my2: {
    marginVertical: 2 * kStyleConst.kSpacing,
  },
  orderFilter: {
    borderBottomWidth: 1,
    borderWidth: 0,
    marginBottom: 40,
    paddingLeft: 0,
    zIndex: 1,
  },
  pageHeaderImage: {
    backgroundColor: kColors.gray,
    height: BANNER_HEIGHT,
  },
  pageSearchBox: {
    alignItems: "center",
    borderBottomWidth: 1,
    borderColor: kColors.grayPink,
    flexDirection: "row",
    flex: 1,
  },
  pageSearchBoxIcon: {
    color: kColors.gray500,
    height: 20,
    marginRight: kStyleConst.kSpacing * 4,
    width: 20,
  },
  pageSearchBoxIconContainer: {
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100%",
  },
  pl3: {
    paddingLeft: 3 * kStyleConst.kSpacing,
  },
  pt1: {
    paddingTop: 1 * kStyleConst.kSpacing,
  },
  searchBox: {
    color: kColors.brandDark,
    fontFamily: "Poppins",
    fontSize: 14,
    height: 40,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    width: "100%",
  },
  sizeMd: {
    height: 120,
    width: 120,
  },
  tagCloseIcon: {
    marginLeft: kStyleConst.kSpacing,
    width: 8,
  },
  textBase: {
    fontSize: 16,
  },
  textCenter: {
    textAlign: "center",
  },
  textSm: {
    fontSize: 14,
  },
  textXs: {
    fontSize: 12,
  },
});
